import { useMemo } from 'react';
import { useCachedSelectedProject } from './useCachedSelectedProject';
import { useAppSelector } from 'store/hooks';
import { EMPTY_SELECTOR } from './cachedSelectorsUtils';
import {
  TargetAudienceRequest,
  TargetAudienceResponse,
  InfoCompletenessScoring,
} from 'store/api/apiTypes';
import { rulesApiSlice } from 'store/api/rulesApiSlice';

/**
 * useCachedTargetAudience
 * Returns cached Target Audience rule analysis if it was previously fetched.
 * Does NOT trigger a network request (pure store selector usage).
 */
export const useCachedTargetAudience = () => {
  const { selectedProject } = useCachedSelectedProject();
  const domain = selectedProject?.domain;
  const activeReport = useAppSelector(state => state.history.activeReport);
  const isGenerating = useAppSelector(
    state => state.reportGeneration.isGenerating
  );

  const queryArgs = useMemo<TargetAudienceRequest | null>(
    () => (domain ? { domain, reportId: activeReport?.id } : null),
    [domain, activeReport]
  );

  const selectTargetAudience = useMemo(
    () =>
      queryArgs
        ? rulesApiSlice.endpoints.targetAudience.select(queryArgs)
        : (EMPTY_SELECTOR as any),
    [queryArgs]
  );
  const selectResult = useAppSelector(selectTargetAudience as any) as {
    data: any;
    status: string;
    isLoading?: boolean;
    error?: any;
  };
  const { data, status, error, isLoading: loading } = selectResult;

  const targetAudience = data as TargetAudienceResponse | undefined;

  let passCount = 0;
  const rulesMap = targetAudience?.company_analysis?.scoring_breakdown as
    | Record<string, InfoCompletenessScoring>
    | undefined;
  if (rulesMap) {
    Object.values(rulesMap).forEach(rule => {
      if (rule.score >= 8) {
        passCount += 1;
      }
    });
  }
  const isLoaded =
    !!targetAudience?.company_analysis && !loading && !isGenerating;
  const isLoading = loading || isGenerating;

  return {
    targetAudience,
    companyAnalysis: targetAudience?.company_analysis,
    productAnalyses: targetAudience?.product_analyses || {},
    companyConfidence:
      targetAudience?.company_analysis?.target_audience_identified
        .confidence_level,
    companyEvidance:
      targetAudience?.company_analysis?.target_audience_identified
        .evidence_strength,
    scoring: targetAudience?.company_analysis?.scoring_breakdown,
    score: targetAudience?.company_analysis?.final_score,
    urlsAnalyzed: targetAudience?.company_analysis?.page_urls_analyzed,
    extraction: targetAudience?.company_analysis?.target_audience_extraction,
    identified: {
      primary:
        targetAudience?.company_analysis?.target_audience_identified
          .primary_target_audience,
      confidence_level:
        targetAudience?.company_analysis?.target_audience_identified
          .confidence_level,
      evidence_strength:
        targetAudience?.company_analysis?.target_audience_identified
          .evidence_strength,
    },
    status,
    isLoading,
    isLoaded,
    error,
    queryArgs,
    passed: passCount,
    rulesCount: Object.keys(
      targetAudience?.company_analysis?.scoring_breakdown || {}
    ).length,
  };
};
